import React, { useState } from 'react';
import {
  FaFacebook,
  FaLinkedin,
  FaInstagram,
  FaXTwitter,
  FaPinterest,
} from 'react-icons/fa6';

// ─── Constants ────────────────────────────────────────────────────────────────

const PLATFORM_META_MAP = {
  facebook:  { icon: FaFacebook,  color: '#1877F2', label: 'Facebook'  },
  linkedin:  { icon: FaLinkedin,  color: '#0A66C2', label: 'LinkedIn'  },
  twitter:   { icon: FaXTwitter,  color: '#1DA1F2', label: 'Twitter'   },
  x:         { icon: FaXTwitter,  color: '#1DA1F2', label: 'X'         },
  instagram: { icon: FaInstagram, color: '#E4405F', label: 'Instagram' },
  pinterest: { icon: FaPinterest, color: '#E60023', label: 'Pinterest' },
};

// ─── PostCard ─────────────────────────────────────────────────────────────────
// Renders a single platform post card with native-style preview UI.

const PostCard = ({ post, isDarkMode, onApprove, onRegenerate, boardPicker = null }) => {
  const platformKey = (post.platform || '').toLowerCase();
  const meta = PLATFORM_META_MAP[platformKey] || {
    icon: FaInstagram, color: '#6366f1', label: post.platform || 'Social',
  };
  const PlatformIcon = meta.icon;

  const isApproved   = post.status === 'approved';
  const isScheduled  = post.status === 'scheduled';
  const isRegenerating = post.status === 'regenerating';
  const isApproving  = post.status === 'approving';
  const isDone       = isApproved || isScheduled;

  const captionText = post.caption || '';
  const hashtagText = Array.isArray(post.hashtags) ? post.hashtags.join(' ') : (post.hashtags || '');
  const isLinkedIn  = platformKey === 'linkedin';
  const isFacebook  = platformKey === 'facebook';

  // ── Shared subcomponents ──────────────────────────────────────────────────

  const CaptionBlock = () => (
    <p className={`text-sm leading-relaxed whitespace-pre-wrap break-words ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
      {captionText}
      {hashtagText && (
        <span className="ml-1 font-medium" style={{ color: meta.color }}>
          {' '}{hashtagText}
        </span>
      )}
    </p>
  );

  const MediaBlock = () => {
    if (!post.imageUrl) return null;
    return (
      <div className="w-full bg-black/10">
        <img
          src={post.imageUrl}
          alt={`Day ${post.day} post visual`}
          className="w-full object-cover"
          style={{ maxHeight: 320 }}
        />
      </div>
    );
  };

  const ActionBar = () => (
    <div className={`px-4 py-3 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
      {isDone ? (
        <div className="flex items-center gap-2 text-green-500">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
          </svg>
          <span className="text-sm font-semibold">
            {isScheduled ? `Scheduled for ${post.scheduledDate ? new Date(post.scheduledDate).toLocaleString() : 'your selected time'}` : 'Approved'}
          </span>
        </div>
      ) : (
        <>
        {boardPicker}
        <div className="flex items-center gap-2">
          <button
            onClick={onApprove}
            disabled={isApproving || isRegenerating}
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold text-white transition-all active:scale-95 disabled:opacity-60"
            style={{ background: `linear-gradient(135deg, ${meta.color}, ${meta.color}cc)` }}
          >
            {isApproving ? (
              <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
            ) : (
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            )}
            {isApproving ? 'Scheduling…' : 'Approve & Schedule'}
          </button>

          <button
            onClick={onRegenerate}
            disabled={isApproving || isRegenerating}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold border transition-all active:scale-95 disabled:opacity-60 ${
              isDarkMode
                ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            {isRegenerating ? (
              <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
            ) : (
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            )}
            {isRegenerating ? 'Regenerating…' : 'Regenerate'}
          </button>
        </div>
        </>
      )}
    </div>
  );

  // ── LinkedIn-style card ───────────────────────────────────────────────────

  if (isLinkedIn) {
    return (
      <div
        className={`rounded-xl overflow-hidden shadow border ${isDarkMode ? 'bg-[#1b1f23] border-[#38434f]' : 'bg-white border-[#e0e0e0]'}`}
        style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}
      >
        <div className="px-4 pt-3 pb-2 flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0" style={{ background: 'linear-gradient(135deg,#0A66C2,#004182)' }}>
            {meta.label[0]}
          </div>
          <div className="flex-1 min-w-0">
            <div className={`text-sm font-semibold leading-tight ${isDarkMode ? 'text-white' : 'text-[#000000e6]'}`}>IGR-Infi-Portal</div>
            <div className={`text-xs leading-tight ${isDarkMode ? 'text-gray-400' : 'text-[#00000099]'}`}>Day {post.day} • <span>🌐</span></div>
          </div>
          <button className="text-xs font-semibold px-3 py-1 rounded-full border border-[#0A66C2] text-[#0A66C2] hover:bg-[#0A66C211]">+ Follow</button>
        </div>
        <div className="px-4 pb-2"><CaptionBlock /></div>
        <MediaBlock />
        <div className={`px-4 py-1 flex items-center gap-1 text-xs ${isDarkMode ? 'text-gray-400' : 'text-[#00000099]'}`}>
          <span>👍❤️🎉</span><span className="ml-1">127</span><span className="ml-auto">335 comments</span>
        </div>
        <div className={`border-t mx-3 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`} />
        <div className={`flex items-center ${isDarkMode ? 'text-gray-400' : 'text-[#00000099]'}`}>
          {[{icon:'👍',label:'Like'},{icon:'💬',label:'Comment'},{icon:'🔁',label:'Repost'},{icon:'📤',label:'Send'}].map(a => (
            <button key={a.label} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-semibold hover:bg-black/5">
              <span>{a.icon}</span>{a.label}
            </button>
          ))}
        </div>
        <ActionBar />
      </div>
    );
  }

  // ── Facebook-style card ───────────────────────────────────────────────────

  if (isFacebook) {
    return (
      <div
        className={`rounded-xl overflow-hidden shadow border ${isDarkMode ? 'bg-[#242526] border-[#3a3b3c]' : 'bg-white border-[#dddfe2]'}`}
        style={{ fontFamily: 'Helvetica, Arial, sans-serif' }}
      >
        <div className="px-4 pt-3 pb-2 flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0" style={{ background: 'linear-gradient(135deg,#1877F2,#0050a0)' }}>T</div>
          <div className="flex-1 min-w-0">
            <div className={`text-sm font-semibold leading-tight ${isDarkMode ? 'text-[#e4e6eb]' : 'text-[#050505]'}`}>Test Page</div>
            <div className={`text-xs leading-tight flex items-center gap-1 ${isDarkMode ? 'text-[#b0b3b8]' : 'text-[#65676b]'}`}>
              Day {post.day} • {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} • <span>🌐</span>
            </div>
          </div>
          <button className={`text-lg ${isDarkMode ? 'text-gray-400' : 'text-[#65676b]'} px-1`}>···</button>
        </div>
        <div className="px-4 pb-2"><CaptionBlock /></div>
        <MediaBlock />
        <div className={`px-4 py-1.5 flex items-center justify-between text-xs ${isDarkMode ? 'text-[#b0b3b8]' : 'text-[#65676b]'}`}>
          <div className="flex items-center gap-1"><span>👍❤️😮</span><span>127</span></div>
          <span>335 comments · 24 shares</span>
        </div>
        <div className={`border-t mx-3 ${isDarkMode ? 'border-[#3a3b3c]' : 'border-[#dddfe2]'}`} />
        <div className="flex items-center">
          {[{icon:'👍',label:'Like'},{icon:'💬',label:'Comment'},{icon:'↗',label:'Share'}].map(a => (
            <button key={a.label} className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-semibold ${isDarkMode ? 'text-[#b0b3b8] hover:bg-[#3a3b3c]' : 'text-[#65676b] hover:bg-[#f2f2f2]'}`}>
              <span>{a.icon}</span>{a.label}
            </button>
          ))}
        </div>
        <ActionBar />
      </div>
    );
  }

  // ── Generic platform card ─────────────────────────────────────────────────

  return (
    <div className={`rounded-xl overflow-hidden shadow border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
      <div className="px-4 py-2.5 flex items-center gap-2" style={{ backgroundColor: `${meta.color}18`, borderBottom: `1px solid ${meta.color}30` }}>
        <PlatformIcon className="w-4 h-4" style={{ color: meta.color }} />
        <span className="text-xs font-semibold" style={{ color: meta.color }}>{meta.label} · Day {post.day}</span>
      </div>
      <MediaBlock />
      <div className="px-4 py-3"><CaptionBlock /></div>
      <ActionBar />
    </div>
  );
};

// ─── GeneratingCard ───────────────────────────────────────────────────────────
// Shown while a post is being generated from n8n.

const GeneratingCard = ({ day, platform, isDarkMode }) => {
  const platformKey = (platform || '').toLowerCase();
  const meta = PLATFORM_META_MAP[platformKey] || { color: '#6366f1', label: platform || 'Social' };

  return (
    <div className={`rounded-xl overflow-hidden border ${isDarkMode ? 'bg-gray-800/60 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
      <div className="px-4 py-2.5 flex items-center gap-2" style={{ backgroundColor: `${meta.color}12`, borderBottom: `1px solid ${meta.color}25` }}>
        <div className="w-4 h-4 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: meta.color, borderTopColor: 'transparent' }} />
        <span className="text-xs font-semibold" style={{ color: meta.color }}>{meta.label} · Day {day} — Generating…</span>
      </div>
      <div className="p-4 space-y-2.5">
        {[80, 60, 70].map((w, i) => (
          <div key={i} className={`h-3 rounded-full animate-pulse ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`} style={{ width: `${w}%` }} />
        ))}
        <div className={`h-32 rounded-lg mt-3 animate-pulse ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
      </div>
    </div>
  );
};

// ─── SocialMediaPostPreview ───────────────────────────────────────────────────
// Main exported component. Renders the full post-by-post preview feed.

/**
 * Props:
 *  posts          – array of post objects:
 *                   { id, day, platform, caption, hashtags, imageUrl, status, scheduledDate }
 *                   status: 'generating' | 'ready' | 'regenerating' | 'approving' | 'approved' | 'scheduled' | 'error'
 *  pendingDays    – array of { day, platform } still being generated (shows skeleton cards)
 *  isDarkMode     – bool
 *  onApprove      – (postId) => void
 *  onRegenerate   – (postId) => void
 *  renderBoardPicker – (post) => ReactNode | null  (Pinterest board selector shown above the actions)
 *  onBack        – () => void  (back to channel selection)
 *  generating     – bool (true while any generation is in flight)
 *  campaignName   – string (shown in header)
 */
const SocialMediaPostPreview = ({
  posts = [],
  pendingDays = [],
  isDarkMode,
  onApprove,
  onRegenerate,
  renderBoardPicker,
  onBack,
  generating = false,
  campaignName = '',
}) => {
  const bubbleBg  = isDarkMode ? 'bg-gray-800' : 'bg-gray-200';
  const textPrimary  = isDarkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const textMuted = isDarkMode ? 'text-gray-500' : 'text-gray-400';

  const approved  = posts.filter(p => p.status === 'approved' || p.status === 'scheduled').length;
  const total     = posts.length + pendingDays.length;

  return (
    <div className="flex items-start gap-2 mb-4">
      {/* Bot avatar */}
      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm ${
        isDarkMode
          ? 'bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-cyan-500/30'
          : 'bg-gradient-to-br from-cyan-100 to-blue-100 border border-cyan-300'
      }`}>
        <svg className="w-4 h-4 text-cyan-500" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 0L13.5 8.5L22 10L13.5 11.5L12 20L10.5 11.5L2 10L10.5 8.5L12 0Z" />
          <path d="M18 4L18.5 6.5L21 7L18.5 7.5L18 10L17.5 7.5L15 7L17.5 6.5L18 4Z" />
        </svg>
      </div>

      <div className="flex-1 min-w-0 max-w-[90%] md:max-w-[80%]">
        {/* Label */}
        <p className={`text-xs font-semibold mb-1.5 ml-1 ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`}>
          ✨ AI Marketing Strategist — Social Media Posts
        </p>

        {/* Main bubble */}
        <div className={`px-4 py-4 rounded-2xl shadow-sm ${bubbleBg} ${isDarkMode ? 'border border-gray-700/50' : ''}`}>
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <h4 className={`text-sm font-bold ${textPrimary}`}>
                {campaignName ? `${campaignName} — ` : ''}Posts Preview
              </h4>
              <p className={`text-xs mt-0.5 ${textSecondary}`}>
                {generating
                  ? `Generating posts one by one… ${posts.length}/${total} ready`
                  : `${approved}/${posts.length} approved`}
              </p>
            </div>

            {/* Progress pill */}
            {total > 0 && (
              <div className={`text-xs px-2.5 py-1 rounded-full font-semibold ${
                isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
              }`}>
                {approved}/{total}
              </div>
            )}
          </div>

          {/* Progress bar */}
          {total > 0 && (
            <div className={`w-full h-1.5 rounded-full mb-4 overflow-hidden ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
              <div
                className="h-full rounded-full transition-all duration-700 ease-out"
                style={{
                  width: `${(posts.length / total) * 100}%`,
                  background: 'linear-gradient(90deg, #06b6d4, #3b82f6)',
                }}
              />
            </div>
          )}

          {/* Post cards feed */}
          <div className="space-y-4">
            {posts.map(post => (
              <PostCard
                key={post.id}
                post={post}
                isDarkMode={isDarkMode}
                onApprove={() => onApprove(post.id)}
                onRegenerate={() => onRegenerate(post.id)}
                boardPicker={post.status === 'error' ? null : renderBoardPicker?.(post)}
              />
            ))}

            {/* Skeleton cards for pending days */}
            {pendingDays.map((p, i) => (
              <GeneratingCard key={`pending-${i}`} day={p.day} platform={p.platform} isDarkMode={isDarkMode} />
            ))}
          </div>

          {/* Summary / back button */}
          {!generating && posts.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-700/30">
              {approved === posts.length ? (
                <div className={`flex items-center gap-2 px-3 py-2 rounded-xl ${isDarkMode ? 'bg-green-500/10 border border-green-500/20' : 'bg-green-50 border border-green-200'}`}>
                  <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-sm font-semibold text-green-500">All posts scheduled! Check your Performance Marketing calendar.</p>
                </div>
              ) : (
                <p className={`text-xs ${textMuted}`}>{posts.length - approved} post{posts.length - approved !== 1 ? 's' : ''} still need approval.</p>
              )}
            </div>
          )}

          {onBack && (
            <button
              onClick={onBack}
              className={`mt-3 flex items-center gap-1.5 text-xs font-medium transition-colors ${isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'}`}
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
              </svg>
              Back to channel selection
            </button>
          )}

          {/* Branding */}
          <div className="flex items-center mt-2">
            <span className={`text-[10px] ${textMuted}`}>⚡ Powered by Infinium</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SocialMediaPostPreview;